/**
 * Issue a service token, as a panel form. The secret is revealed once in a
 * dialog; closing it opens the new token's record in the same panel.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import { issueServiceToken } from '@optum/api-client/endpoints/service-tokens';
import type { ServiceTokenIssue } from '@optum/api-client/types';

import { PanelFormActions, PanelFormFields, usePanelFormDone } from '../../components/shell/PanelForm.js';
import { Field } from '../../ui/Field.js';
import { Input, Typography } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';
import { TokenRevealDialog } from './TokenRevealDialog.js';

export function IssueTokenForm() {
  const qc = useQueryClient();
  const toast = useToast();
  const done = usePanelFormDone();
  const [label, setLabel] = useState('');
  // Blank means the platform's default, which is the path we want an operator
  // to take. A date is offered, not demanded.
  const [expiresAt, setExpiresAt] = useState('');
  const [issued, setIssued] = useState<ServiceTokenIssue | null>(null);
  // The token is a secret returned once, so the agent asks for a fresh sign-in.
  const [guardedIssue, stepUpDialog] = useStepUp(issueServiceToken);

  const issue = useMutation({
    mutationFn: () =>
      guardedIssue(label, expiresAt ? new Date(expiresAt).toISOString() : undefined),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: qk.serviceTokens() });
      setIssued(t);
    },
    onError: (e) => toast.error('Issue failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <>
      <PanelFormFields onSubmit={() => issue.mutate()}>
        <Typography variant="body-muted">
          A bearer credential for a CI/CD workflow. Its scope is always plugins.
        </Typography>
        <Field id="token-label" label="Label" required hint="Short name — e.g. ci-plugin-publisher.">
          <Input
            id="token-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            autoFocus
          />
        </Field>
        <Field
          id="token-expires"
          label="Expires"
          hint="Leave blank for the platform default. The token stops working on this date."
        >
          <Input
            id="token-expires"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </Field>
        <PanelFormActions
          submitLabel="Issue token"
          pending={issue.isPending}
          disabled={!label}
          dirty={(label !== '' || expiresAt !== '') && issued === null}
        />
      </PanelFormFields>
      {stepUpDialog}
      <TokenRevealDialog
        secret={issued}
        onClose={() => issued && done({ kind: 'service-token', id: issued.id })}
      />
    </>
  );
}
